import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { customFetch } from "@workspace/api-client-react";
import { Gift, Pencil, Plus, Trash2, BadgeDollarSign, Percent, Truck, Package, Coins } from "lucide-react";
import {
  LOYALTY_DEFAULTS,
  LOYALTY_VALIDITY_OPTIONS,
  normalizeLoyaltyConfig,
  type LoyaltyConfig,
  type LoyaltyReward,
  type LoyaltyRewardType,
} from "@workspace/pricing";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { CurrencyInput } from "@/components/ui/currency-input";
import { ModalShell } from "@/components/ui/modal-shell";
import { UltraBadge } from "@/components/UltraBadge";
import { TIPO_PREMIO, fmtPts } from "@/lib/loyalty";

const DAY_SHORT = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];
const novoId = () => Math.random().toString(36).slice(2, 10);

const TIPOS: Array<{ type: LoyaltyRewardType; icon: typeof Gift }> = [
  { type: "fixed", icon: BadgeDollarSign },
  { type: "percent", icon: Percent },
  { type: "free_delivery", icon: Truck },
  { type: "gift", icon: Package },
  { type: "cashback", icon: Coins },
];

function nomeSugerido(type: LoyaltyRewardType, value: number): string {
  if (type === "fixed") return value > 0 ? `R$ ${value.toFixed(2).replace(".", ",").replace(",00", "")} de desconto` : "";
  if (type === "percent") return value > 0 ? `${value}% de desconto` : "";
  if (type === "free_delivery") return "Frete grátis";
  if (type === "cashback") return "Usar pontos como dinheiro";
  return "";
}

function valorDoPremio(r: LoyaltyReward): string {
  if (r.type === "fixed") return `R$ ${r.value.toFixed(2).replace(".", ",")}`;
  if (r.type === "percent") return `${r.value}%`;
  if (r.type === "cashback") return `1 pt = R$ ${r.value.toFixed(2).replace(".", ",")}`;
  return "";
}

// Programa de pontos (Ultra, 05/10/2026) em Configurações. A config inteira vai num campo só (loyaltyConfig) e o
// auto-save da seção grava; o servidor normaliza e cuida do "desde quando" do estoque de cada prêmio.
export function LoyaltySection({
  enabled,
  config: rawConfig,
  podeUsar,
  mostrarSelo,
  onChange,
}: {
  enabled: boolean;
  config: unknown;
  podeUsar: boolean;
  mostrarSelo: boolean;
  onChange: (patch: { loyaltyEnabled?: boolean; loyaltyConfig?: LoyaltyConfig }) => void;
}) {
  // A tela edita a config CRUA (campo vazio enquanto digita, linha nova ainda sem nome); quem normaliza é o
  // servidor ao gravar. Normalizar aqui sumia com a linha recém-adicionada.
  const cfg = useMemo(
    () => ({ ...LOYALTY_DEFAULTS, ...((rawConfig && typeof rawConfig === "object" ? rawConfig : {}) as Partial<LoyaltyConfig>) }) as LoyaltyConfig,
    [rawConfig],
  );
  const setCfg = (patch: Partial<Record<keyof LoyaltyConfig, unknown>>) =>
    onChange({ loyaltyConfig: { ...cfg, ...patch } as LoyaltyConfig });
  const numOuVazio = (v: string, min: number) => (v === "" ? "" : Math.max(min, Math.trunc(Number(v) || min)));
  const [editando, setEditando] = useState<LoyaltyReward | null>(null);

  const { data: estoque } = useQuery<Array<{ id: string; stockLeft: number | null }>>({
    queryKey: ["loyalty-rewards-stock"],
    queryFn: () => customFetch("/api/loyalty/rewards"),
    enabled: enabled && cfg.rewards.some((r) => r.stock != null),
    staleTime: 30_000,
  });
  const restam = (id: string) => estoque?.find((e) => e.id === id)?.stockLeft ?? null;

  const salvarPremio = (r: LoyaltyReward) => {
    const existe = cfg.rewards.some((x) => x.id === r.id);
    setCfg({ rewards: existe ? cfg.rewards.map((x) => (x.id === r.id ? r : x)) : [...cfg.rewards, r] });
    setEditando(null);
  };

  return (
    <Card className="p-6 space-y-4">
      <div className="flex items-center gap-2 text-lg font-semibold flex-wrap">
        <Gift className="w-5 h-5 text-primary" /> Programa de pontos
        {mostrarSelo && <UltraBadge />}
      </div>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <Label className="text-sm font-medium">Ligar programa de pontos</Label>
          <p className="text-xs text-muted-foreground mt-1">
            Cada pedido entregue rende pontos que o cliente troca pelos prêmios abaixo.
          </p>
        </div>
        <Switch
          className="mt-0.5 shrink-0"
          checked={enabled}
          disabled={!enabled && !podeUsar}
          onCheckedChange={(v) => onChange(v ? { loyaltyEnabled: true, loyaltyConfig: normalizeLoyaltyConfig(cfg) } : { loyaltyEnabled: false })}
          data-testid="settings-loyalty-enabled"
        />
      </div>

      {enabled && (
        <>
          <div className="space-y-2 border-t pt-4">
            <Label className="text-sm font-medium">Como o cliente ganha</Label>
            <div className="flex gap-2">
              {(["real", "pedido"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setCfg({ earnMode: m })}
                  className={`flex-1 px-3 py-2 rounded-full text-sm font-semibold border transition-colors ${
                    cfg.earnMode === m ? "bg-primary text-primary-foreground border-primary" : "bg-background border-border hover:bg-muted"
                  }`}
                  data-testid={`settings-loyalty-mode-${m}`}
                >
                  {m === "real" ? "Por real gasto" : "Por pedido"}
                </button>
              ))}
            </div>
            {cfg.earnMode === "real" ? (
              // "N pontos a cada R$ X" (Adega do tendays, 05/10: "a cada compra de 15 reais, um ponto"). X = R$ 1 é o de antes.
              <div className="flex items-center gap-2 flex-wrap">
                <Input
                  type="number"
                  min={1}
                  className="w-16 h-10 text-right"
                  value={cfg.pointsPerReal}
                  onChange={(e) => setCfg({ pointsPerReal: numOuVazio(e.target.value, 1) })}
                  aria-label="Pontos"
                  data-testid="settings-loyalty-rate"
                />
                <span className="text-sm whitespace-nowrap">{Number(cfg.pointsPerReal) === 1 ? "ponto" : "pontos"} a cada</span>
                <CurrencyInput
                  id="settings-loyalty-step"
                  className="w-28 h-10 text-right"
                  value={cfg.realStep || 1}
                  onChange={(v) => setCfg({ realStep: Number(v) || 1 })}
                  placeholder="R$ 1,00"
                />
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <Label className="text-sm font-normal">Pontos por pedido</Label>
                <Input
                  type="number"
                  min={1}
                  className="w-24 h-10 text-right"
                  value={cfg.pointsPerOrder}
                  onChange={(e) => setCfg({ pointsPerOrder: numOuVazio(e.target.value, 1) })}
                  data-testid="settings-loyalty-rate"
                />
              </div>
            )}
            <div className="flex items-center justify-between gap-3">
              <Label className="text-sm font-normal">Pedido mínimo para pontuar</Label>
              <CurrencyInput
                className="w-32 h-10 text-right"
                value={cfg.minOrder || ""}
                onChange={(v) => setCfg({ minOrder: Number(String(v).replace(",", ".")) || 0 })}
                placeholder="Sem mínimo"
              />
            </div>
            <div className="flex items-center justify-between gap-3">
              <Label className="text-sm font-normal">Validade dos pontos</Label>
              <select
                className="border rounded-md p-2 text-sm bg-background h-10"
                value={cfg.validityDays == null ? "" : String(cfg.validityDays)}
                onChange={(e) => setCfg({ validityDays: e.target.value === "" ? null : Number(e.target.value) })}
                data-testid="settings-loyalty-validity"
              >
                {LOYALTY_VALIDITY_OPTIONS.map((d) => (
                  <option key={String(d)} value={d == null ? "" : String(d)}>
                    {d == null ? "Não vencem" : `${d} dias`}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-2 border-t pt-4">
            <Label className="text-sm font-medium">Prêmios</Label>
            {cfg.rewards.length === 0 && (
              <p className="text-xs text-muted-foreground">Cadastre o que o cliente pode ganhar com os pontos.</p>
            )}
            <ul className="divide-y divide-border/60">
              {cfg.rewards.map((r) => {
                const sobra = r.stock != null ? restam(r.id) : null;
                return (
                  <li key={r.id} className="flex items-center gap-3 py-2">
                    <div className={`flex-1 min-w-0 ${r.active ? "" : "opacity-50"}`}>
                      <p className="text-sm font-medium truncate">{r.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {[TIPO_PREMIO[r.type] !== r.name ? TIPO_PREMIO[r.type] : "", valorDoPremio(r)].filter(Boolean).join(" · ")}
                        {r.stock != null ? ` · ${sobra != null ? `restam ${sobra}` : `${r.stock} disponíveis`}` : ""}
                        {!r.active ? " · pausado" : ""}
                      </p>
                    </div>
                    <span className="text-sm font-semibold tabular-nums whitespace-nowrap">
                      {r.type === "cashback" ? (r.cost > 0 ? `mín. ${fmtPts(r.cost)}` : "") : fmtPts(r.cost)}
                    </span>
                    <button
                      type="button"
                      onClick={() => setEditando(r)}
                      className="p-2 rounded-md hover:bg-muted text-muted-foreground"
                      aria-label={`Editar ${r.name}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  </li>
                );
              })}
            </ul>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() =>
                setEditando({ id: novoId(), name: "", type: "fixed", cost: 100, value: 10, stock: null, stockSetAt: null, active: true })
              }
              data-testid="settings-loyalty-add-reward"
            >
              <Plus className="w-4 h-4 mr-1" /> Adicionar prêmio
            </Button>
          </div>

          <div className="space-y-2 border-t pt-4">
            <Label className="text-sm font-medium">Outras formas de ganhar</Label>
            <p className="text-xs text-muted-foreground">Aparecem para o cliente e viram atalho no "Dar pontos" da página Clientes.</p>
            {cfg.earnWays.map((w, i) => (
              <div key={w.id} className="flex items-center gap-2">
                <Input
                  className="flex-1 h-10"
                  value={w.name}
                  placeholder="Ex.: Postar e marcar a loja no Instagram"
                  maxLength={80}
                  onChange={(e) => setCfg({ earnWays: cfg.earnWays.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)) })}
                />
                <Input
                  type="number"
                  min={1}
                  className="w-24 h-10 text-right"
                  value={w.points}
                  onChange={(e) =>
                    setCfg({ earnWays: cfg.earnWays.map((x, j) => (j === i ? { ...x, points: numOuVazio(e.target.value, 1) } : x)) })
                  }
                  aria-label="Pontos"
                />
                <button
                  type="button"
                  className="p-2 rounded-md hover:bg-muted text-muted-foreground"
                  aria-label="Remover"
                  onClick={() => setCfg({ earnWays: cfg.earnWays.filter((_, j) => j !== i) })}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setCfg({ earnWays: [...cfg.earnWays, { id: novoId(), name: "", points: 50 }] })}
            >
              <Plus className="w-4 h-4 mr-1" /> Adicionar
            </Button>
          </div>

          <div className="space-y-3 border-t pt-4">
            <Label className="text-sm font-medium">Bônus</Label>
            {(
              [
                ["bonusFirstOrder", "No 1º pedido"],
                ["bonusBirthday", "Na semana do aniversário"],
                ["referralPoints", "Para quem indica (no 1º pedido do indicado)"],
              ] as const
            ).map(([k, rotulo]) => (
              <div key={k} className="flex items-center justify-between gap-3">
                <Label className="text-sm font-normal">{rotulo}</Label>
                <Input
                  type="number"
                  min={0}
                  className="w-24 h-10 text-right"
                  value={cfg[k] || ""}
                  placeholder="0"
                  onChange={(e) => setCfg({ [k]: numOuVazio(e.target.value, 0) })}
                  data-testid={`settings-loyalty-${k}`}
                />
              </div>
            ))}
            <div className="space-y-2">
              <Label className="text-sm font-normal">Pontos em dobro nos dias</Label>
              <div className="flex flex-wrap gap-1.5">
                {DAY_SHORT.map((rotulo, d) => {
                  const on = cfg.doubleDays.includes(d);
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => setCfg({ doubleDays: on ? cfg.doubleDays.filter((x) => x !== d) : [...cfg.doubleDays, d] })}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
                        on ? "bg-primary text-primary-foreground border-primary" : "bg-background text-muted-foreground border-border hover:bg-muted"
                      }`}
                    >
                      {rotulo}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex items-start justify-between gap-3 border-t pt-4">
            <div className="min-w-0">
              <Label className="text-sm font-medium">Um prêmio por pedido</Label>
              <p className="text-xs text-muted-foreground mt-1">Desligado, o cliente pode juntar prêmios diferentes no mesmo pedido.</p>
            </div>
            <Switch className="mt-0.5 shrink-0" checked={cfg.oneRewardPerOrder} onCheckedChange={(v) => setCfg({ oneRewardPerOrder: v })} />
          </div>
          <div className="flex items-start justify-between gap-3 border-t pt-4">
            <div className="min-w-0">
              <Label className="text-sm font-medium">Prêmio junto com cupom</Label>
              <p className="text-xs text-muted-foreground mt-1">O cliente usa prêmio e cupom no mesmo pedido.</p>
            </div>
            <Switch className="mt-0.5 shrink-0" checked={cfg.allowWithCoupon} onCheckedChange={(v) => setCfg({ allowWithCoupon: v })} />
          </div>
        </>
      )}

      {editando && (
        <PremioDialog
          inicial={editando}
          restamAgora={editando.stock != null ? restam(editando.id) : null}
          novo={!cfg.rewards.some((r) => r.id === editando.id)}
          onCancel={() => setEditando(null)}
          onSave={salvarPremio}
          onDelete={() => {
            setCfg({ rewards: cfg.rewards.filter((r) => r.id !== editando.id) });
            setEditando(null);
          }}
        />
      )}
    </Card>
  );
}

function PremioDialog({
  inicial,
  restamAgora,
  novo,
  onCancel,
  onSave,
  onDelete,
}: {
  inicial: LoyaltyReward;
  /** Unidades que sobram agora (o campo mostra isto; mudar o número recomeça a contagem dali). */
  restamAgora: number | null;
  novo: boolean;
  onCancel: () => void;
  onSave: (r: LoyaltyReward) => void;
  onDelete: () => void;
}) {
  const [r, setR] = useState<LoyaltyReward>(() =>
    inicial.stock != null && restamAgora != null ? { ...inicial, stock: restamAgora } : inicial,
  );
  const [nomeTocado, setNomeTocado] = useState(!novo);
  const set = (patch: Partial<LoyaltyReward>) =>
    setR((prev) => {
      const next = { ...prev, ...patch };
      if (!nomeTocado && (patch.type !== undefined || patch.value !== undefined)) {
        next.name = nomeSugerido(next.type, next.value) || next.name;
      }
      return next;
    });
  const valido =
    r.name.trim().length > 0 &&
    (r.type === "cashback" ? r.value > 0 : r.cost >= 1) &&
    (r.type !== "fixed" || r.value > 0) &&
    (r.type !== "percent" || (r.value >= 1 && r.value <= 100));

  return (
    <ModalShell
      open
      onOpenChange={(o) => {
        if (!o) onCancel();
      }}
      title={novo ? "Novo prêmio" : "Editar prêmio"}
      size="sm"
      footer={
        <div className="flex gap-2 w-full">
          {!novo && (
            <Button type="button" variant="outline" className="text-destructive" onClick={onDelete}>
              <Trash2 className="w-4 h-4" />
            </Button>
          )}
          <Button type="button" variant="outline" className="flex-1" onClick={onCancel}>
            Cancelar
          </Button>
          <Button type="button" className="flex-1" disabled={!valido} onClick={() =>
              onSave({
                ...r,
                name: r.name.trim(),
                // Número intocado = mantém a contagem que já existe.
                stock: inicial.stock != null && restamAgora != null && r.stock === restamAgora ? inicial.stock : r.stock,
              })
            } data-testid="loyalty-reward-save">
            Salvar
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-2">
          {TIPOS.map(({ type, icon: Icon }) => (
            <button
              key={type}
              type="button"
              onClick={() => set({ type, value: type === "percent" ? 10 : type === "cashback" ? 0.1 : type === "fixed" ? 10 : 0, cost: type === "cashback" ? 0 : r.cost || 100 })}
              className={`flex flex-col items-center gap-1 rounded-xl border-2 px-2 py-2.5 text-xs font-semibold transition-colors ${
                r.type === type ? "border-primary bg-primary/5" : "border-border hover:bg-muted"
              }`}
              data-testid={`loyalty-reward-type-${type}`}
            >
              <Icon className="w-5 h-5" />
              {TIPO_PREMIO[type]}
            </button>
          ))}
        </div>

        {(r.type === "fixed" || r.type === "percent" || r.type === "cashback") && (
          <div className="flex items-center justify-between gap-3">
            <Label className="text-sm font-normal">
              {r.type === "fixed" ? "Valor do desconto" : r.type === "percent" ? "Desconto (%)" : "Cada ponto vale"}
            </Label>
            {r.type === "percent" ? (
              <Input
                type="number"
                min={1}
                max={100}
                className="w-24 h-10 text-right"
                value={r.value || ""}
                onChange={(e) => set({ value: Math.min(100, Math.max(0, Math.trunc(Number(e.target.value) || 0))) })}
              />
            ) : r.type === "fixed" ? (
              <CurrencyInput className="w-32 h-10 text-right" value={r.value || ""} onChange={(v) => set({ value: Number(String(v).replace(",", ".")) || 0 })} />
            ) : (
              <Input
                inputMode="decimal"
                className="w-32 h-10 text-right"
                value={String(r.value || "").replace(".", ",")}
                placeholder="0,10"
                onChange={(e) => set({ value: Number(e.target.value.replace(",", ".")) || 0 })}
                data-testid="loyalty-reward-cashback-rate"
              />
            )}
          </div>
        )}

        <div className="space-y-1.5">
          <Label className="text-sm font-normal" htmlFor="loyalty-reward-name">
            {r.type === "gift" ? "Qual é o brinde" : "Nome que o cliente vê"}
          </Label>
          <Input
            id="loyalty-reward-name"
            value={r.name}
            maxLength={60}
            placeholder={r.type === "gift" ? "Ex.: Açaí 300 ml" : ""}
            onChange={(e) => {
              setNomeTocado(true);
              setR({ ...r, name: e.target.value });
            }}
            data-testid="loyalty-reward-name"
          />
        </div>

        <div className="flex items-center justify-between gap-3">
          <Label className="text-sm font-normal">{r.type === "cashback" ? "Mínimo de pontos para usar" : "Custa (pontos)"}</Label>
          <Input
            type="number"
            min={r.type === "cashback" ? 0 : 1}
            className="w-28 h-10 text-right"
            value={r.cost || (r.type === "cashback" ? "" : "")}
            placeholder={r.type === "cashback" ? "0" : "100"}
            onChange={(e) => set({ cost: Math.max(0, Math.trunc(Number(e.target.value) || 0)) })}
            data-testid="loyalty-reward-cost"
          />
        </div>

        <div className="flex items-center justify-between gap-3">
          <Label className="text-sm font-normal">Unidades disponíveis</Label>
          <Input
            type="number"
            min={0}
            className="w-28 h-10 text-right"
            value={r.stock ?? ""}
            placeholder="Sem limite"
            onChange={(e) => set({ stock: e.target.value === "" ? null : Math.max(0, Math.trunc(Number(e.target.value) || 0)) })}
          />
        </div>

        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Label className="text-sm font-medium">Disponível</Label>
            <p className="text-xs text-muted-foreground mt-1">Pausado, some do link e do PDV.</p>
          </div>
          <Switch className="mt-0.5 shrink-0" checked={r.active} onCheckedChange={(v) => set({ active: v })} />
        </div>
      </div>
    </ModalShell>
  );
}
